import { useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  TAX_SUMMARY_MONTH_KEYS,
  TAX_SUMMARY_MONTH_LABELS,
  resolveTaxSummaryDrilldownCellSource,
} from "@/lib/taxSummaryWorkbookConstants";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function safeFilePart(value, max = 40) {
  return String(value || "reporte")
    .replace(/[^\w\-áéíóúÁÉÍÓÚñÑ]+/g, "_")
    .replace(/_+/g, "_")
    .slice(0, max);
}

/**
 * Panel de relación CFDI para una fila del resumen (solo lectura).
 * Descarga Excel de revisión con columnas según el módulo.
 */
export default function TaxSummaryCfdiDetailPanel({
  fiscalClientId,
  year,
  rowId,
  rowLabel = "",
  workbook,
  onClose,
}) {
  const [monthKey, setMonthKey] = useState("all");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  const cellSource = useMemo(
    () => resolveTaxSummaryDrilldownCellSource(workbook, rowId, monthKey),
    [workbook, rowId, monthKey],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await despachoFiscalInfoApi.getTaxSummaryCfdiDetail({
          fiscalClientId,
          year,
          rowId,
          monthKey,
          cellSource,
        });
        if (!cancelled) setDetail(data?.detail || null);
      } catch (err) {
        if (!cancelled) {
          setDetail(null);
          setError(parseFiscalInfoError(err)?.message || "No se pudo cargar el desglose.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fiscalClientId, year, rowId, monthKey, cellSource]);

  const items = detail?.items || [];
  const isManual = detail?.mode === "manual_review";
  const amountLabel = detail?.report_profile?.amount_label || "Monto concepto";
  const moduleLabel = detail?.report_profile?.module_label || "";

  const handleDownloadReport = async () => {
    if (downloading || isManual || !items.length) return;
    setDownloading(true);
    try {
      const blob = await despachoFiscalInfoApi.downloadTaxSummaryCfdiDetail({
        fiscalClientId,
        year,
        rowId,
        monthKey,
        cellSource,
      });
      const mk = monthKey && monthKey !== "all" ? monthKey : "anual";
      const fname = `Revision_${safeFilePart(moduleLabel || rowLabel || rowId, 28)}_${year}_${mk}.xlsx`;
      triggerBlobDownload(blob, fname);
      toast.success("Reporte Excel descargado para revisión.");
    } catch (err) {
      toast.error(parseFiscalInfoError(err)?.message || "No se pudo descargar el reporte.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="rounded-sm border border-slate-200 bg-slate-50/90 p-3 space-y-2"
      data-testid="tax-summary-cfdi-detail-panel"
      data-row-id={rowId}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-slate-800 m-0">
            Relación CFDI · {rowLabel || rowId}
            {moduleLabel ? (
              <span className="ml-1 font-normal text-slate-500">({moduleLabel})</span>
            ) : null}
          </p>
          <p className="text-[10px] text-slate-500 m-0">
            Solo consulta. No modifica montos ni cálculos del resumen.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={monthKey} onValueChange={setMonthKey}>
            <SelectTrigger className="h-7 w-[110px] text-[11px]" data-testid="tax-summary-cfdi-detail-month">
              <SelectValue placeholder="Mes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el año</SelectItem>
              {TAX_SUMMARY_MONTH_KEYS.filter((k) => k !== "acumulado").map((k) => (
                <SelectItem key={k} value={k}>
                  {TAX_SUMMARY_MONTH_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-[11px]"
            disabled={loading || downloading || isManual || !items.length}
            onClick={handleDownloadReport}
            data-testid="tax-summary-cfdi-detail-excel"
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
            ) : (
              <Download className="h-3.5 w-3.5 mr-1" aria-hidden />
            )}
            Descargar reporte Excel
          </Button>
          {onClose ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-[11px]"
              onClick={onClose}
            >
              Cerrar
            </Button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[11px] text-slate-500 py-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Cargando relación…
        </div>
      ) : null}

      {error ? (
        <p className="text-[11px] text-rose-700 m-0" data-testid="tax-summary-cfdi-detail-error">
          {error}
        </p>
      ) : null}

      {!loading && !error && isManual ? (
        <div
          className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950"
          data-testid="tax-summary-cfdi-detail-manual"
        >
          {detail?.message || "Cálculo manual — revise sus datos."}
        </div>
      ) : null}

      {!loading && !error && !isManual && detail?.message ? (
        <p className="text-[10px] text-slate-600 m-0">{detail.message}</p>
      ) : null}

      {!loading && !error && !isManual ? (
        <>
          <div className="overflow-auto max-h-56 rounded-sm border border-slate-200 bg-white">
            <table className="w-full border-collapse text-[10px]">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="p-1.5 text-left border-b">Fecha</th>
                  <th className="p-1.5 text-left border-b">Folio</th>
                  <th className="p-1.5 text-left border-b">Nombre</th>
                  <th className="p-1.5 text-left border-b">Origen</th>
                  <th className="p-1.5 text-right border-b">Sub</th>
                  <th className="p-1.5 text-right border-b">IVA</th>
                  <th className="p-1.5 text-right border-b">Total</th>
                  <th className="p-1.5 text-right border-b">{amountLabel}</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? (
                  items.map((row) => (
                    <tr key={`${row.uuid}-${row.month_key}-${row.origen}`} className="border-b border-slate-100">
                      <td className="p-1.5 whitespace-nowrap">{row.fecha || "—"}</td>
                      <td className="p-1.5 whitespace-nowrap">{row.folio || "—"}</td>
                      <td className="p-1.5 max-w-[180px] truncate" title={row.nombre || ""}>
                        {row.nombre || "—"}
                      </td>
                      <td className="p-1.5 whitespace-nowrap">{row.origen || "—"}</td>
                      <td className="p-1.5 text-right tabular-nums">{formatMoney(row.subtotal)}</td>
                      <td className="p-1.5 text-right tabular-nums">{formatMoney(row.iva)}</td>
                      <td className="p-1.5 text-right tabular-nums">{formatMoney(row.total)}</td>
                      <td className="p-1.5 text-right tabular-nums font-medium">{formatMoney(row.amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="p-3 text-center text-slate-500">
                      No hay CFDI en precarga para este concepto/periodo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {items.length ? (
            <p className="text-[10px] text-slate-600 m-0" data-testid="tax-summary-cfdi-detail-totals">
              {detail.count} comprobante(s) · Sub {formatMoney(detail.sum_subtotal)} · IVA{" "}
              {formatMoney(detail.sum_iva)} · Total {formatMoney(detail.sum_total)} · {amountLabel}{" "}
              {formatMoney(detail.sum_amount)}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
